import React, { useState } from 'react';
import { Button } from './ui/button';
import { Upload, FileText, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { key: 'zalohova_faktura', label: 'Zálohová faktura' },
  { key: 'danovy_doklad', label: 'Daňový doklad' },
  { key: 'faktura', label: 'Faktura' }
];

const DocumentManager = ({ applicationId, documents = {}, onUpdate }) => {
  const [uploading, setUploading] = useState({});

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [documentType]: true }));

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/documents/${applicationId}/upload-document?document_type=${documentType}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Dokument úspěšně nahrán');
        onUpdate && onUpdate();
      }
    } catch (error) {
      toast.error('Chyba při nahrávání dokumentu');
      console.error('Error uploading document:', error);
    } finally {
      setUploading(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleDelete = async (documentType) => {
    if (!window.confirm('Opravdu chcete smazat tento dokument?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(
        `${BACKEND_URL}/api/documents/${applicationId}/delete-document/${documentType}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast.success('Dokument smazán');
      onUpdate && onUpdate();
    } catch (error) {
      toast.error('Chyba při mazání dokumentu');
      console.error('Error deleting document:', error);
    }
  };

  return (
    <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
      <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Dokumenty pro prodejce
      </h4>
      <p className="text-sm text-green-800 mb-4">
        Nahrajte faktury a doklady. Prodejce je uvidí na své statusové stránce.
      </p>

      <div className="space-y-3">
        {DOCUMENT_TYPES.map(({ key, label }) => {
          const doc = documents[key];
          const isUploading = uploading[key];

          return (
            <div key={key} className="bg-white p-3 rounded border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-sm text-gray-900">{label}</p>
                  {doc ? (
                    <div className="flex items-center gap-2 mt-1">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-gray-600">{doc.filename}</span>
                      <span className="text-xs text-gray-400">
                        • {new Date(doc.uploaded_at).toLocaleDateString('cs-CZ')}
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Žádný dokument nahrán</p>
                  )}
                </div>

                <div className="flex gap-2">
                  {doc ? (
                    <>
                      <a
                        href={`${BACKEND_URL}${doc.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex"
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-600"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-600"
                        onClick={() => handleDelete(key)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <label className="cursor-pointer">
                      <span className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md bg-green-500 hover:bg-green-600 text-white transition-colors">
                        <Upload className="w-4 h-4 mr-1" />
                        {isUploading ? 'Nahrávám...' : 'Nahrát'}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(key, file);
                          e.target.value = ''; // Reset input
                        }}
                        disabled={isUploading}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentManager;
